<template>
  <div>
    <div v-if="organizationLoader">
      <div class="text-center py-4">
        <b-spinner
          type="grow"
          :label="$t('DEBTIA_COMMON_LOADING')"
        />
      </div>
    </div>
    <div v-else-if="!organizationLoader && organizationOptions.length && selectedOrganization.expires_in !== 'Expired'">
      <div
        v-if="selectedOrganization && selectedOrganization.expire_alert === true && selectedOrganization.expires_in !== 'Expired'"
        class="card rounded p-2"
      >
        <b-row class="align-items-center justify-content-between">
          <b-col cols="9">
            <p class="mb-0 text-danger">
              {{ $t('DEBTIA_ORGANIZATION_EXPIRY_NOTE') }} <strong>{{ selectedOrganization.label }}</strong>
              {{ $t('DEBTIA_ORGANIZATION_EXPIRY_NOTE_2') }} <strong>{{ selectedOrganization.expires_in }}</strong>.
            </p>
          </b-col>
        </b-row>
      </div>
      <div class="card rounded mb-1 p-2">
        <b-row class="d-flex justify-content-end">
          <b-col
            class="d-flex align-items-center justify-content-start mb-1"
          >
            <h3>{{ $t('DEBTIA_SELECT_ORGANIZATION') }}</h3>
          </b-col>
          <b-col>
            <v-select
              v-model="organizations"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="organizationOptions"
              :reduce="val => val.value"
              :clearable="false"
              @input="setOrganization"
            />
          </b-col>
          <b-col cols="auto">
            <b-button
              variant="primary"
              size="sm"
              class="font-weight-bold d-flex align-items-center"
              @click="confirmationModal"
            >
              <feather-icon
                icon="PlusIcon"
                size="18"
                class="mr-50"
              />
              <span class="align-middle">{{ $t('DEBTIA_ADD_MORE') }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-card
        class="mb-0"
      >
          <div class="mb-1">
            <b-row>
              <b-col
                cols="12"
                class="d-flex align-items-center justify-content-between"
              >
                <div class="d-flex align-items-center">
                  <label>{{ $t('DEBTIA_SHOWING') }}</label>

                  <v-select
                    v-model="invoicePerPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="false"
                    class="search_by_select per-page-selector d-inline-block mx-50"
                    :options="perPageOptions"
                  />
                  <label>{{ $t('DEBTIA_ENTRIES') }}</label>
                </div>

                <b-form-checkbox
                  v-model="autoImport"
                  switch
                  @change="ToggleAutoImport($event)"
                >
                  {{ $t('DEBTIA_AUTO_IMPORT_EVERYTIME') }}
                </b-form-checkbox>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col>
              <h3 class="mb-1">
                {{ $t('DEBTIA_INVOICES') }}
              </h3>
            </b-col>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1"
            >
              <b-button
                variant="primary"
                size="sm"
                @click="importAllInvoices"
              >
                {{ $t('DEBTIA_IMPORT_ALL') }}
              </b-button>
            </b-col>
          </b-row>
          <b-table
            class="position-relative resource-listing"
            responsive
            :fields="invoicesFields"
            :items="invoiceData"
            hover
            show-empty
            :empty-text="$t('DEBTIA_COMMON_NO_MATCHING_RECORDS')"
          >
            <template #cell(invoice_number)="data">
              {{ (data.item.Number ) ? data.item.Number : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(contact_name)="data">
              {{ (data.item.ContactName ) ? data.item.ContactName : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(payment_status)="data">
              {{ (data.item.PaymentStatus) ? data.item.PaymentStatus == 'Overdue' ? $t('DEBTIA_OVER_DUE') : $t('DEBTIA_DUE') : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(payment_date)="data">
              {{ formattedDate(data.item.PaymentDate) ? formattedDate(data.item.PaymentDate) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(gross_amount)="data">
              {{ (data.item.TotalInclVat) ? convertToDKK(data.item.TotalInclVat, data.item.Currency) : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(action)="data">
              <b-badge
                v-if="data.item.isImported === false"
                pill
                variant="light-warning"
                :class="{ 'disabled': data.item.PaymentStatus !== 'Overdue' }"
                style="cursor: pointer;"
                @click="data.item.PaymentStatus === 'Overdue' && importSingleInvoice(data.item.Guid)"
              >
                {{ $t('DEBTIA_IMPORT') }}
              </b-badge>
              <b-badge
                v-else
                pill
                variant="light-success"
              >
                {{ $t('DEBTIA_IMPORTED') }}
              </b-badge>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">{{ $t('DEBTIA_SHOWING') }} {{ invoiceFrom }} to {{ invoiceTo }} of {{ invoiceTotalItems }} {{ $t('DEBTIA_ENTRIES') }}</span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >

                <b-pagination
                  v-model="invoiceCurrentPage"
                  :total-rows="invoiceTotalItems"
                  :per-page="invoicePerPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
      </b-card>

      <b-card
        class="mb-0 mt-3"
      >
          <div class="mb-1">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              >
                <label>{{ $t('DEBTIA_SHOWING') }}</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  class="search_by_select per-page-selector d-inline-block mx-50"
                  :options="perPageOptions"
                />
                <label>{{ $t('DEBTIA_ENTRIES') }}</label>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col>
              <h3 class="mb-1">
                {{ $t('DEBTIA_DEBTORS') }}
              </h3>
            </b-col>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1"
            >
              <b-button
                variant="primary"
                size="sm"
                @click="importAllDebtors"
              >
                {{ $t('DEBTIA_IMPORT_ALL') }}
              </b-button>
            </b-col>
          </b-row>
          <b-table
            class="position-relative resource-listing"
            responsive
            :fields="debtorsFields"
            :items="debtorsData"
            hover
            show-empty
            :empty-text="$t('DEBTIA_COMMON_NO_MATCHING_RECORDS')"
          >
            <template #cell(customerNumber)="data">
              {{ (data.item.ContactGuid ) ? data.item.ContactGuid : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(name)="data">
              {{ (data.item.Name ) ? data.item.Name : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(email)="data">
              {{ (data.item.Email ) ? data.item.Email : $t('DEBTIA_COMMON_NA') }}
            </template>
            <template #cell(action)="data">
              <b-badge
                v-if="data.item.isImported === false"
                pill
                variant="light-warning"
                style="cursor: pointer;"
                @click="importSingleDebtor(data.item.ContactGuid)"
              >
                {{ $t('DEBTIA_IMPORT') }}
              </b-badge>
              <b-badge
                v-else
                pill
                variant="light-success"
              >
                {{ $t('DEBTIA_IMPORTED') }}
              </b-badge>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">{{ $t('DEBTIA_SHOWING') }} {{ from }} to {{ to }} of {{ totalItems }} {{ $t('DEBTIA_ENTRIES') }}</span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >

                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalItems"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
      </b-card>
    </div>
    <div v-else-if="!organizationLoader && selectedOrganization && selectedOrganization.expires_in === 'Expired'">
      <div class="misc-wrapper">
        <div class="misc-inner p-2 p-sm-3">
          <div class="w-100 text-center">
            <div class="mb-2">
              <p>{{ $t('DEBTIA_COMPANY_EXPIRE_NOTE') }}</p>
              <b-button
                variant="primary"
                @click="redirectLinkForDinero"
              >
                {{ $t('DEBTIA_INTEGRATE') }}
              </b-button>
            </div>
            <b-img
              fluid
              :src="imgUrl"
              :alt="$t('DEBTIA_COMMON_COMING_SOON')"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="misc-wrapper">
        <div class="misc-inner p-2 p-sm-3">
          <div class="w-100 text-center">
            <div class="mb-2">
              <p>{{ $t('DEBTIA_DINERO_NOTE') }}</p>
              <b-button
                variant="primary"
                @click="redirectLinkForDinero"
              >
                {{ $t('DEBTIA_INTEGRATE') }}
              </b-button>
            </div>
            <b-img
              fluid
              :src="imgUrl"
              :alt="$t('DEBTIA_COMMON_COMING_SOON')"
            />
          </div>
        </div>
      </div>
    </div>

    <integrations-modal
      ref="integrationsModal"
      :redirect-link="redirectLinkForDinero"
    />
  </div>
</template>

<script>
/* eslint-disable global-require */
import {
  BRow, BCol, BButton, BTable, BCard, BPagination, BBadge, BImg, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@axios'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store/index'
import constants from '@/app-configs/constants'
import integrationsModal from './ConfirmationModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BTable,
    BCard,
    BPagination,
    vSelect,
    BBadge,
    BImg,
    BSpinner,
    integrationsModal,
    BFormCheckbox,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      from: 0,
      to: 0,
      totalItems: 0,
      loader: false,
      organizations: '',
      organizationOptions: [],
      debtorsData: [],
      invoiceData: [],
      invoiceCurrentPage: 1,
      invoicePerPage: 10,
      invoiceFrom: 0,
      invoiceTo: 0,
      invoiceTotalItems: 0,
      clientId: JSON.parse(localStorage.getItem('userData')).data.id,
      debtorsLoader: false,
      invoiceLoader: false,
      perPageOptions: [10, 25, 50, 100],
      downImg: require('@/assets/images/pages/coming-soon.svg'),
      organizationLoader: true,
      autoImport: true,
    }
  },
  computed: {
    debtorsFields() {
      return [
        { key: 'customerNumber', sortable: true, label: i18n.t('DEBTIA_CUSTOMER_NUMBER') },
        { key: 'name', sortable: true, label: i18n.t('DEBTIA_NAME') },
        { key: 'email', sortable: true, label: i18n.t('DEBTIA_EMAIL') },
        { key: 'action', label: i18n.t('DEBTIA_ACTION') },
      ]
    },
    invoicesFields() {
      return [
        { key: 'invoice_number', sortable: true, label: i18n.t('DEBTIA_INVOICE_NUMBER') },
        { key: 'contact_name', sortable: true, label: i18n.t('DEBTIA_CONTACT_NAME') },
        { key: 'payment_status', sortable: true, label: i18n.t('DEBTIA_PAYMENT_STATUS') },
        { key: 'payment_date', sortable: true, label: i18n.t('DEBTIA_DUE_DATE') },
        { key: 'gross_amount', sortable: true, label: i18n.t('DEBTIA_AMOUNT') },
        { key: 'action', label: i18n.t('DEBTIA_ACTION') },
      ]
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.downImg = require('@/assets/images/pages/error-dark.svg')
        return this.downImg
      }
      return this.downImg
    },
    selectedOrganization() {
      return this.organizationOptions.find(org => org.value === this.organizations) || null
    },
  },
  watch: {
    invoiceLoader(val) {
      this.$store.commit('app/SET_GLOBAL_LOADING', { show: val || this.debtorsLoader, text: this.$t('DEBTIA_INTEGRATION_LOADING') })
    },
    debtorsLoader(val) {
      this.$store.commit('app/SET_GLOBAL_LOADING', { show: val || this.invoiceLoader, text: this.$t('DEBTIA_INTEGRATION_LOADING') })
    },
    currentPage: {
      handler() {
        this.getDebtors()
      },
    },
    perPage: {
      handler() {
        this.currentPage = 1
        this.getDebtors()
      },
    },
    invoicePerPage: {
      handler() {
        this.invoiceCurrentPage = 1
        this.getInvoices()
      },
    },
    invoiceCurrentPage: {
      handler() {
        this.getInvoices()
      },
    },
  },
  created() {
    this.getOrganizations()
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  methods: {
    formattedDate(date) {
      if (!date) return this.$t('DEBTIA_COMMON_INVALID_DATE')
      // Convert the date to a string if it's not already one
      const dateString = String(date)
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-')
        return `${day}-${month}-${year}`
      }
      // Handle invalid date format or other cases
      return this.$t('DEBTIA_COMMON_INVALID_DATE')
    },
    getOrganizations() {
      this.organizationLoader = true
      axios.get('integrations/dinero/get-organizations').then(response => {
        const data = Array.isArray(response.data.data) ? response.data.data : []
        this.organizationOptions = data
        if (this.organizationOptions.length > 0) {
          this.getDebtors()
          this.getInvoices()
        }
        const selected = data.find(org => org.is_selected === 1)
        if (selected) {
          this.organizations = selected.value
          this.autoImport = !!selected.auto_import
        }
        this.organizationLoader = false
      }).catch(error => {
        this.organizationLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_ORGANIZATIONS'),
          },
        })
      })
    },
    setOrganization() {
      axios.post('user-settings/set-default-organization', { organization_id: this.organizations, source: 'dinero' }).then(response => {
        if (response.data.status === 200) {
          this.getDebtors()
          this.getInvoices()
        }
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_SET_ORGANIZATION'),
          },
        })
      })
    },
    getDebtors() {
      this.debtorsLoader = true
      axios
        .get(`integrations/dinero/get-debtors?per_page=${this.perPage}&page=${this.currentPage - 1}`)
        .then(response => {
          this.debtorsData = response.data.data.debtors
          const { page } = response.data.data
          const perPage = response.data.data.per_page
          const { total } = response.data.data
          this.totalItems = response.data.data.total
          this.from = total === 0 ? 0 : page * perPage + 1
          this.to = Math.min((page + 1) * perPage, total)
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_ERROR'),
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_DEBTORS'),
            },
          })
        })
        .finally(() => {
          this.debtorsLoader = false
        })
    },
    getInvoices() {
      this.invoiceLoader = true
      axios.get(`integrations/dinero/get-invoices?per_page=${this.invoicePerPage}&page=${this.invoiceCurrentPage - 1}`).then(response => {
        this.invoiceData = response.data.data.invoices
        const { page } = response.data.data
        const perPage = response.data.data.per_page
        const { total } = response.data.data
        this.invoiceFrom = total === 0 ? 0 : page * perPage + 1
        this.invoiceTo = Math.min((page + 1) * perPage, total)
        this.invoiceTotalItems = response.data.data.total
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_LOAD_INVOICES'),
          },
        })
      }).finally(() => {
        this.invoiceLoader = false
      })
    },
    redirectLinkForDinero() {
      const url = `${constants.dineroRedirectUrl}&state=${this.clientId}`
      window.location.href = url
    },
    importSingleDebtor(id) {
      this.debtorsLoader = true
      const url = `integrations/dinero/import-debtor/${id}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedDebtors = response.data.data.failed_to_import_debtors || []
          if (failedDebtors.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedDebtors.map(debtor => `Failed Debtor: ${debtor.contactGuid}: ${debtor.error}`).join('')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: response.data.message || this.$t('DEBTIA_TOAST_DEBTOR_IMPORTED'),
              },
            })
          }
          this.debtorsLoader = false
          this.currentPage = 1
          this.getDebtors()
        }
      }).catch(error => {
        this.debtorsLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_DEBTOR'),
          },
        })
      })
    },
    importAllDebtors() {
      this.debtorsLoader = true
      const url = `integrations/dinero/import-all-debtors?per_page=${this.perPage}&page=${this.currentPage - 1}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedDebtors = response.data.data.failed_to_import_debtors || []
          if (failedDebtors.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedDebtors.map(debtor => `Failed Debtor: ${debtor.contactGuid}: ${debtor.error}`).join('')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: response.data.message || this.$t('DEBTIA_TOAST_DEBTOR_IMPORTED'),
              },
            })
          }
          this.debtorsLoader = false
          this.getDebtors()
        }
      }).catch(error => {
        this.debtorsLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_DEBTOR'),
          },
        })
      })
    },
    importSingleInvoice(invoiceId) {
      this.invoiceLoader = true
      const url = `integrations/dinero/import-invoice/${invoiceId}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_SUCCESS'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('DEBTIA_TOAST_INVOICE_IMPORTED'),
            },
          })
          this.invoiceLoader = false
          this.invoiceCurrentPage = 1
          this.getInvoices()
          this.getDebtors()
        }
      }).catch(error => {
        this.invoiceLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_INVOICE'),
          },
        })
      })
    },
    importAllInvoices() {
      this.invoiceLoader = true
      const url = `integrations/dinero/import-all-invoices?per_page=${this.invoicePerPage}&page=${this.invoiceCurrentPage - 1}`
      axios.get(url).then(response => {
        if (response.data.status === 200) {
          const failedInvoices = response.data.data.failed_to_import_invoices || []
          if (failedInvoices.length > 0) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_ERROR'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: `${failedInvoices.map(invoice => `Invoice ${invoice.Number}: ${invoice.error}`).join(', ')}`,
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('DEBTIA_COMMON_SUCCESS'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('DEBTIA_TOAST_ALL_INVOICES_IMPORTED'),
              },
            })
          }
          this.invoiceLoader = false
          this.getInvoices()
          this.getDebtors()
        }
      }).catch(error => {
        this.invoiceLoader = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.response.data.message || this.$t('DEBTIA_TOAST_FAILED_IMPORT_INVOICE'),
          },
        })
      })
    },
    confirmationModal() {
      this.$refs.integrationsModal.open()
    },
    convertToDKK(amount, defaultCurrency) {
      return `${amount} ${defaultCurrency}`
      // return new Intl.NumberFormat('da-DK', { style: 'currency', currency: defaultCurrency }).format(parseFloat(amount).toFixed(2))
    },
    ToggleAutoImport(val) {
      const url = `integrations/organizations/${this.organizations}/auto-import`
      axios.post(url, { auto_import: val, source: 'dinero' }).then(response => {
        if (response.data.status === 200) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('DEBTIA_COMMON_SUCCESS'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('DEBTIA_TOAST_AUTO_IMPORT_UPDATED'),
            },
          })
        }
      }).catch(error => {
        const msg = Array.isArray(error.response.data.message) ? error.response.data.message.join(', ') : ''
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_COMMON_ERROR'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: msg || this.$t('DEBTIA_TOAST_FAILED_UPDATE_AUTO_IMPORT'),
          },
        })
      })
    },
  },

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.search_by_select .vs__dropdown-toggle{
  width: 160px;
}

.resource-listing .table-hover {
  tbody {
    tr {
      cursor: default;
    }
  }
}

</style>
